import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { PROSPECT_ACTIVITY_LABELS } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Loader2, Search, Building2, MapPin, User, CalendarClock, Plus, AlertTriangle, Rocket, Star, Clock } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_STATUSES = ["Da contattare", "Contattato", "In sviluppo", "Interessato", "Non interessato", "Da richiamare", "Convertito in Lead", "Sospeso", "Archiviato"];
const ACT_TYPES = ["telefonata", "visita", "email", "meet", "evento", "networking", "invio_materiale", "follow_up", "altro"];
const CONVERT_STATUS = "Convertito in Lead";
const DUE_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

function dueSoon(iso) {
  if (!iso) return false;
  return new Date(iso).getTime() <= Date.now() + DUE_WINDOW_MS;
}
function dueTodayOrOverdue(iso) {
  if (!iso) return false;
  const end = new Date(); end.setHours(23, 59, 59, 999);
  return new Date(iso).getTime() <= end.getTime();
}
function activityBadge(iso) {
  if (!iso) return null;
  const now = new Date();
  const d = new Date(iso);
  const sameDay = d.toDateString() === now.toDateString();
  let tone = "bg-secondary text-muted-foreground";
  let label = d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
  if (d < now && !sameDay) { tone = "bg-red-500/15 text-red-500"; label = `Scaduta ${label}`; }
  else if (sameDay) { tone = "bg-amber-500/15 text-amber-500"; label = "Oggi"; }
  return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${tone}`}><CalendarClock className="h-3 w-3" /> {label}</span>;
}
function featuredOrLast(p) {
  const tl = p.timeline || [];
  if (!tl.length) return null;
  const feat = p.featured_activity_id ? tl.find((a) => a.id === p.featured_activity_id) : null;
  if (feat) return { a: feat, featured: true };
  const last = tl.reduce((m, a) => (new Date(a.created_at) > new Date(m.created_at) ? a : m), tl[0]);
  return { a: last, featured: false };
}

export default function PipelineProspect() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
  const [cats, setCats] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ category: "", province: "", assigned_user_id: "", search: "" });
  const [onlyDue, setOnlyDue] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [converting, setConverting] = useState(null);
  const [quickFor, setQuickFor] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [notePopup, setNotePopup] = useState(null);
  const [quickAct, setQuickAct] = useState({ type: "telefonata", note: "", next_action_date: "" });
  const [savingAct, setSavingAct] = useState(false);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (filters.category) p.set("category", filters.category);
    if (filters.province) p.set("province", filters.province);
    if (filters.assigned_user_id) p.set("assigned_user_id", filters.assigned_user_id);
    if (filters.search) p.set("search", filters.search);
    api.get(`/prospects?${p.toString()}`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [filters]);

  useEffect(() => {
    api.get("/settings/prospect").then((r) => setStatuses(r.data.statuses || DEFAULT_STATUSES)).catch(() => {});
    api.get("/prospect-categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const usersMap = useMemo(() => Object.fromEntries((users || []).map((u) => [u.id, u.name])), [users]);
  const catColor = useMemo(() => Object.fromEntries((cats || []).map((c) => [c.key, c.color])), [cats]);
  const provinces = useMemo(() => Array.from(new Set((items || []).map((p) => p.province).filter(Boolean))).sort(), [items]);
  const todoTodayCount = useMemo(() => (items || []).filter((p) => dueTodayOrOverdue(p.next_activity_at)).length, [items]);

  const byStatus = useMemo(() => {
    const m = {}; statuses.forEach((s) => { m[s] = []; });
    const src = onlyDue ? (items || []).filter((p) => dueSoon(p.next_activity_at)) : (items || []);
    src.forEach((p) => { (m[p.status] ||= []).push(p); });
    return m;
  }, [items, statuses, onlyDue]);

  const onDrop = async (status) => {
    setDragOver(null);
    const id = dragId; setDragId(null);
    if (!id) return;
    const p = (items || []).find((x) => x.id === id);
    if (!p || p.status === status) return;

    if (status === CONVERT_STATUS) {
      if (p.converted_lead_id) { navigate(`/leads/${p.converted_lead_id}`); return; }
      setConverting(id);
      try {
        const { data } = await api.post(`/prospects/${id}/convert-to-lead`, { origin_activity_type: "visita" });
        setItems((arr) => arr.map((x) => (x.id === id ? data.prospect : x)));
        toast.success(`"${p.company_name}" convertito in Lead`);
        navigate(`/leads/${data.lead_id}`);
      } catch (e) {
        const det = e.response?.data?.detail;
        if (det && typeof det === "object") {
          toast.error(det.message || "Conversione non riuscita");
          if (det.lead_id) navigate(`/leads/${det.lead_id}`);
        } else toast.error(formatApiError(det));
      } finally { setConverting(null); }
      return;
    }

    const prev = items;
    // Non spostiamo subito: apriamo la finestra "Registra attività".
    // Lo stato cambierà SOLO dopo aver salvato l'azione che ha determinato lo spostamento.
    setPendingStatus(status);
    setQuickFor(p);
    setQuickAct({ type: "telefonata", note: "", next_action_date: "" });
    void prev;
  };

  const submitQuick = async () => {
    if (!quickAct.note.trim()) { toast.error("Inserisci una breve descrizione"); return; }
    setSavingAct(true);
    try {
      const { data } = await api.post(`/prospects/${quickFor.id}/actions`, {
        type: quickAct.type, note: quickAct.note.trim(), next_action_date: quickAct.next_action_date || null,
      });
      if (data.needs_improvement) {
        toast.error(data.messaggio || "Descrizione troppo sintetica: aggiungi qualche dettaglio");
        setSavingAct(false); return;
      }
      let updated = data.prospect;
      // Se l'attività è stata registrata per confermare uno spostamento in pipeline, aggiorna ora lo stato.
      if (pendingStatus && pendingStatus !== updated.status) {
        try {
          const { data: sp } = await api.patch(`/prospects/${quickFor.id}/status`, { status: pendingStatus });
          updated = sp;
          toast.success(`Attività registrata · "${quickFor.company_name}" → ${pendingStatus}`);
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail));
        }
      } else {
        toast.success("Attività registrata");
      }
      setItems((arr) => arr.map((x) => (x.id === quickFor.id ? updated : x)));
      setQuickFor(null); setPendingStatus(null); setQuickAct({ type: "telefonata", note: "", next_action_date: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSavingAct(false); }
  };

  return (
    <div data-testid="prospect-pipeline-page">
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold">Pipeline Prospect</h1>
          <p className="text-xs text-muted-foreground">Trascina tra gli stati (registra l'attività per confermare) · rilascia su "{CONVERT_STATUS}" per generare il Lead</p>
        </div>
        <button
          onClick={() => setOnlyDue(true)}
          data-testid="pp-todo-today"
          title="Mostra solo prospect con attività scaduta o in scadenza"
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 transition-colors ${todoTodayCount > 0 ? "border-red-500/40 bg-red-500/10 hover:bg-red-500/20" : "border-border bg-secondary/40"}`}
        >
          <AlertTriangle className={`h-4 w-4 ${todoTodayCount > 0 ? "text-red-500" : "text-muted-foreground"}`} />
          <span className={`font-heading font-bold text-lg leading-none ${todoTodayCount > 0 ? "text-red-500" : ""}`} data-testid="pp-todo-today-count">{todoTodayCount}</span>
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground leading-tight text-left">Da lavorare<br />oggi</span>
        </button>
      </header>

      <div className="p-4 lg:p-6 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Cerca azienda / città…" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} className="pl-8 w-56" data-testid="pp-search" />
          </div>
          <Select value={filters.category || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, category: v === "all" ? "" : v }))}>
            <SelectTrigger className="w-44" data-testid="pp-filter-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutte le categorie</SelectItem>{cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.province || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, province: v === "all" ? "" : v }))}>
            <SelectTrigger className="w-36" data-testid="pp-filter-province"><SelectValue placeholder="Provincia" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutte le province</SelectItem>{provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.assigned_user_id || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, assigned_user_id: v === "all" ? "" : v }))}>
            <SelectTrigger className="w-44" data-testid="pp-filter-user"><SelectValue placeholder="Assegnatario" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti gli assegnatari</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant={onlyDue ? "default" : "outline"} size="sm" onClick={() => setOnlyDue((v) => !v)} data-testid="pp-filter-overdue" className="gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" /> Solo scadute / in scadenza
          </Button>
        </div>

        {items === null ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pp-board">
            {statuses.map((s) => {
              const isConvertCol = s === CONVERT_STATUS;
              return (
                <div
                  key={s}
                  data-testid={`pp-col-${s}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(s); }}
                  onDragLeave={() => setDragOver((cur) => (cur === s ? null : cur))}
                  onDrop={(e) => { e.preventDefault(); onDrop(s); }}
                  className={`shrink-0 w-64 rounded-lg border transition-colors ${dragOver === s ? (isConvertCol ? "border-emerald-500 bg-emerald-500/10" : "border-primary bg-primary/5") : "border-border bg-secondary/20"}`}
                >
                  <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
                    <span className="text-xs font-heading font-bold uppercase tracking-wider">{s}</span>
                    <span className="text-[10px] font-bold text-muted-foreground bg-background rounded-full px-1.5 py-0.5">{byStatus[s]?.length || 0}</span>
                  </div>
                  <div className="p-2 space-y-2 min-h-[120px]">
                    {(byStatus[s] || []).map((p) => (
                      <Card
                        key={p.id}
                        data-testid={`pp-card-${p.id}`}
                        draggable
                        onDragStart={() => setDragId(p.id)}
                        onDragEnd={() => { setDragId(null); setDragOver(null); }}
                        onClick={() => navigate(`/prospects/${p.id}`)}
                        className={`rounded-md border-border p-2.5 cursor-grab active:cursor-grabbing hover:border-primary/50 transition-colors ${dragId === p.id ? "opacity-50" : ""} ${converting === p.id ? "opacity-60" : ""}`}
                      >
                        <div className="flex items-start gap-2">
                          <span className="mt-1 h-2 w-2 rounded-full shrink-0" style={{ background: catColor[p.category] || "#9CA3AF" }} />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold leading-tight truncate flex items-center gap-1"><Building2 className="h-3 w-3 text-muted-foreground shrink-0" /> {p.company_name}</div>
                            {(p.city || p.province) && <div className="text-[11px] text-muted-foreground mt-0.5 truncate"><MapPin className="h-3 w-3 inline mr-0.5" />{[p.city, p.province].filter(Boolean).join(" · ")}</div>}
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              {activityBadge(p.next_activity_at)}
                              {p.assigned_user_id && usersMap[p.assigned_user_id] && <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1"><User className="h-3 w-3" />{usersMap[p.assigned_user_id]}</span>}
                            </div>
                            {(() => {
                              const f = featuredOrLast(p);
                              if (!f) return null;
                              const a = f.a;
                              return (
                                <div className="mt-1.5 flex items-center gap-1 text-[11px] min-w-0" data-testid={`pp-activity-${p.id}`}>
                                  {f.featured ? <Star className="h-3 w-3 text-amber-400 fill-amber-400 shrink-0" /> : <Clock className="h-3 w-3 text-muted-foreground shrink-0" />}
                                  <span className="text-muted-foreground truncate"><span className="font-medium text-foreground">{PROSPECT_ACTIVITY_LABELS[a.type] || a.type}</span>{a.note ? ` · ${a.note}` : ""}</span>
                                  {a.note && <button onClick={(e) => { e.stopPropagation(); setNotePopup({ p, a, featured: f.featured }); }} title="Vedi nota completa" data-testid={`pp-note-magnify-${p.id}`} className="shrink-0 text-muted-foreground hover:text-primary"><Search className="h-3 w-3" /></button>}
                                </div>
                              );
                            })()}
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); setQuickFor(p); setQuickAct({ type: "telefonata", note: "", next_action_date: "" }); }}
                            title="Registra attività rapida"
                            data-testid={`pp-quick-${p.id}`}
                            className="shrink-0 h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {dragId && (() => {
        const dp = (items || []).find((x) => x.id === dragId);
        if (dp && dp.converted_lead_id) return null;
        return (
          <div
            data-testid="pp-convert-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragOver(CONVERT_STATUS); }}
            onDragLeave={() => setDragOver((cur) => (cur === CONVERT_STATUS ? null : cur))}
            onDrop={(e) => { e.preventDefault(); onDrop(CONVERT_STATUS); }}
            className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-5 py-3 rounded-full border-2 border-dashed shadow-lg backdrop-blur transition-colors ${dragOver === CONVERT_STATUS ? "border-emerald-500 bg-emerald-500/25 text-emerald-500 scale-105" : "border-emerald-500/60 bg-background/90 text-emerald-500"}`}
          >
            <Rocket className="h-4 w-4" /> <span className="text-sm font-semibold">Rilascia qui per convertire in Lead</span>
          </div>
        );
      })()}

      <Dialog open={!!quickFor} onOpenChange={(o) => { if (!o) { setQuickFor(null); setPendingStatus(null); } }}>
        <DialogContent className="max-w-md" data-testid="pp-quick-dialog">
          <DialogHeader>
            <DialogTitle>{pendingStatus ? "Registra attività per lo spostamento" : "Attività rapida"}</DialogTitle>
            <DialogDescription>
              {pendingStatus
                ? `${quickFor?.company_name} — per spostare in "${pendingStatus}" devi registrare l'attività che ha determinato il cambio di stato.`
                : `${quickFor?.company_name} — registra un contatto e imposta la prossima azione.`}
            </DialogDescription>
          </DialogHeader>
          {pendingStatus && (
            <div data-testid="pp-move-banner" className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-primary">
              <Rocket className="h-3.5 w-3.5 shrink-0" />
              <span>Spostamento in <strong>{pendingStatus}</strong> in attesa: verrà applicato al salvataggio dell'attività.</span>
            </div>
          )}
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Tipo</Label>
              <Select value={quickAct.type} onValueChange={(v) => setQuickAct((a) => ({ ...a, type: v }))}>
                <SelectTrigger data-testid="pp-quick-type"><SelectValue /></SelectTrigger>
                <SelectContent>{ACT_TYPES.map((t) => <SelectItem key={t} value={t}>{PROSPECT_ACTIVITY_LABELS[t] || t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Descrizione</Label>
              <Textarea rows={3} value={quickAct.note} onChange={(e) => setQuickAct((a) => ({ ...a, note: e.target.value }))} placeholder="Cosa è emerso dal contatto…" data-testid="pp-quick-note" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Prossima attività (opzionale)</Label>
              <Input type="date" value={quickAct.next_action_date} onChange={(e) => setQuickAct((a) => ({ ...a, next_action_date: e.target.value }))} data-testid="pp-quick-date" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => { setQuickFor(null); setPendingStatus(null); }}>Annulla</Button>
            <Button onClick={submitQuick} disabled={savingAct} data-testid="pp-quick-save">{savingAct && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {pendingStatus ? "Registra e sposta" : "Registra"}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!notePopup} onOpenChange={(o) => { if (!o) setNotePopup(null); }}>
        <DialogContent className="max-w-md" data-testid="pp-note-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">{notePopup?.featured ? <Star className="h-4 w-4 text-amber-400 fill-amber-400" /> : <Clock className="h-4 w-4 text-muted-foreground" />}{notePopup?.p?.company_name}</DialogTitle>
            <DialogDescription>{notePopup?.a ? `${PROSPECT_ACTIVITY_LABELS[notePopup.a.type] || notePopup.a.type}${notePopup.a.created_at ? " · " + new Date(notePopup.a.created_at).toLocaleString("it-IT") : ""}` : ""}</DialogDescription>
          </DialogHeader>
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{notePopup?.a?.note}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
